import React from 'react';
import { BarChart2, Calculator, Calendar, MapPin } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';
import { calculateDateDifference } from '../../../../../utils/calculations/date';

/**
 * Компонент боковой панели для быстрой настройки параметров командировки
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  russianRegions,
  foreignCountries
}) => {
  // Расчет продолжительности поездки
  const startDate = new Date(formData.startDate);
  const endDate = new Date(formData.endDate);
  let tripDuration = 0;
  
  try {
    if (startDate && endDate && !isNaN(startDate) && !isNaN(endDate)) {
      const dateDiff = calculateDateDifference(startDate, endDate);
      // Добавляем 1 день, т.к. в командировочных учитывается и день начала, и день окончания
      tripDuration = dateDiff.days + 1;
    }
  } catch (error) {
    console.error('Error calculating trip duration:', error);
  }
  
  // Расчет текущей ставки суточных
  const dailyRate = parseFloat(formData.customDailyRate) || 0;
  
  // Обработчик изменения даты начала
  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      startDate: newStartDate
    }));
    
    // Убираем ошибку, если она есть
    if (errors.startDate) {
      delete errors.startDate;
    }
  };
  
  // Обработчик изменения даты окончания
  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      endDate: newEndDate
    }));
    
    // Убираем ошибку, если она есть
    if (errors.endDate) {
      delete errors.endDate;
    }
  };
  
  // Обработчик изменения типа командировки
  const handleDestinationChange = (destinationType) => {
    setFormData(prev => {
      const newState = { ...prev, destination: destinationType };
      
      // Обновляем ставку суточных, если не установлен ручной режим
      if (!prev.rateOverride) {
        if (destinationType === 'domestic') {
          const selectedRegion = russianRegions.find(r => r.id === prev.region);
          if (selectedRegion) {
            newState.customDailyRate = selectedRegion.dailyRate.toString();
          }
        } else {
          // Для международных, если уже выбрана страна используем её ставку, 
          // иначе установим Германию как значение по умолчанию
          if (prev.country !== 'none') {
            const selectedCountry = foreignCountries.find(c => c.id === prev.country);
            if (selectedCountry) {
              newState.customDailyRate = selectedCountry.dailyRate.toString();
            }
          } else {
            const defaultCountry = foreignCountries.find(c => c.id === 'germany');
            if (defaultCountry) {
              newState.customDailyRate = defaultCountry.dailyRate.toString();
              newState.country = 'germany';
            }
          }
        }
      }
      
      return newState;
    });
  };
  
  // Обработчик изменения региона РФ
  const handleRegionChange = (regionId) => {
    const selectedRegion = russianRegions.find(r => r.id === regionId);
    
    setFormData(prev => {
      const newState = { ...prev, region: regionId };
      
      // Обновляем ставку суточных, если не установлен ручной режим и это внутрироссийская командировка
      if (!prev.rateOverride && prev.destination === 'domestic' && selectedRegion) {
        newState.customDailyRate = selectedRegion.dailyRate.toString();
      }
      
      return newState;
    });
  };
  
  // Обработчик изменения страны
  const handleCountryChange = (countryId) => {
    const selectedCountry = foreignCountries.find(c => c.id === countryId);
    
    setFormData(prev => {
      const newState = { ...prev, country: countryId };
      
      // Обновляем ставку суточных, если не установлен ручной режим и это зарубежная командировка
      if (!prev.rateOverride && prev.destination === 'international' && selectedCountry) {
        newState.customDailyRate = selectedCountry.dailyRate.toString();
      }
      
      return newState;
    });
  };
  
  // Обработчик изменения ставки суточных
  const handleDailyRateChange = (e) => {
    const newRate = e.target.value;
    
    // Если ставка не задана вручную, нужно включить ручной режим
    setFormData(prev => ({
      ...prev,
      customDailyRate: newRate,
      rateOverride: true
    }));
  };
  
  // Предопределенные продолжительности командировок для быстрого выбора
  const predefinedDurations = [
    { days: 1, label: '1 день' },
    { days: 3, label: '3 дня' },
    { days: 5, label: '5 дней' },
    { days: 7, label: '1 неделя' }
  ];
  
  // Функция установки длительности командировки
  const setTripDuration = (days) => {
    const newStartDate = new Date();
    const newEndDate = new Date();
    newEndDate.setDate(newStartDate.getDate() + days - 1); // -1 т.к. день начала тоже учитывается
    
    setFormData(prev => ({
      ...prev,
      startDate: newStartDate.toISOString().split('T')[0],
      endDate: newEndDate.toISOString().split('T')[0]
    }));
  };
  
  // Пять популярных регионов России
  const popularRussianRegions = [
    'moscow',
    'petersburg',
    'krasnodar',
    'sochi',
    'vladivostok'
  ];
  
  // Пять популярных стран для зарубежных командировок
  const popularForeignCountries = [
    'germany',
    'china',
    'usa',
    'turkey',
    'belarus'
  ];
  
  // Функция расчета
  const calculatePerDiem = () => {
    // Вызываем кнопку Submit в форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Тип командировки */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип командировки</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleDestinationChange('domestic')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.destination === 'domestic' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center gap-1">
                <MapPin className="w-4 h-4" />
                По России
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleDestinationChange('international')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.destination === 'international' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center gap-1">
                <Calendar className="w-4 h-4" />
                Зарубежная
              </div>
            </button>
          </div>
        </div>
        
        {/* Место назначения */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">
              {formData.destination === 'domestic' ? 'Регион' : 'Страна'}
            </label>
          </div>
          
          {formData.destination === 'domestic' && (
            <div className="grid grid-cols-3 gap-2">
              {popularRussianRegions.map(regionId => {
                const region = russianRegions.find(r => r.id === regionId);
                if (!region) return null;
                
                return (
                  <button
                    key={regionId}
                    type="button"
                    onClick={() => handleRegionChange(regionId)}
                    className={`p-2 text-xs rounded-lg border transition-colors
                      ${formData.region === regionId 
                        ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                  >
                    {region.name.split(' ')[0]}
                  </button>
                );
              })}
            </div>
          )}
          
          {formData.destination === 'international' && (
            <div className="grid grid-cols-3 gap-2">
              {popularForeignCountries.map(countryId => {
                const country = foreignCountries.find(c => c.id === countryId);
                if (!country) return null;
                
                return (
                  <button
                    key={countryId}
                    type="button"
                    onClick={() => handleCountryChange(countryId)}
                    className={`p-2 text-xs rounded-lg border transition-colors
                      ${formData.country === countryId 
                        ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                  >
                    {country.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Даты и продолжительность */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Период командировки</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {isNaN(tripDuration) ? '0' : tripDuration} дн.
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <input
                type="date"
                value={formData.startDate}
                onChange={handleStartDateChange}
                className="form-input w-full text-sm"
                placeholder="Начало"
              />
            </div>
            <div>
              <input
                type="date"
                value={formData.endDate}
                onChange={handleEndDateChange}
                className="form-input w-full text-sm"
                placeholder="Окончание"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-4 gap-1">
            {predefinedDurations.map(duration => (
              <button
                key={duration.days}
                type="button"
                onClick={() => setTripDuration(duration.days)}
                className="px-2 py-1 text-xs rounded border bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {duration.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Ставка суточных */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label htmlFor="quickDailyRate" className="text-gray-600 dark:text-gray-400">
              Ставка суточных
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(dailyRate, 0)}
            </span>
          </div>
          
          <input
            type="range"
            id="quickDailyRate"
            min={formData.destination === 'domestic' ? '100' : '1000'}
            max={formData.destination === 'domestic' ? '5000' : '10000'}
            step={formData.destination === 'domestic' ? '100' : '500'}
            value={dailyRate}
            onChange={handleDailyRateChange}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                customDailyRate: formData.destination === 'domestic' ? '700' : '2500',
                rateOverride: true 
              }))}
              className="px-2 py-1 text-xs rounded border transition-colors bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
            >
              Норматив НДФЛ {formData.destination === 'domestic' ? '(700 ₽)' : '(2500 ₽)'}
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                rateOverride: false 
              }))}
              className="px-2 py-1 text-xs rounded border transition-colors bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              Системные ставки
            </button>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculatePerDiem}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать суточные
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;